import { BaseBridge } from "./core/base-bridge.js";
import { DEFAULT_PROTOCOL_VERSION } from "./core/constants.js";
import { WindowTransport } from "./core/transport.js";
import type {
  Bridge,
  BridgeEnvironment,
  Envelope,
  HostBridgeConfig,
  MessageEventLike,
  WindowLike,
} from "./core/types.js";

export type {
  Bridge,
  BridgeEnvironment,
  Envelope,
  EnvelopeKind,
  FitPayload,
  HostBridgeConfig,
  MessageContext,
  MessageHandler,
  PostTarget,
  RequestOptions,
  ResizePayload,
  Transport,
} from "./core/types.js";
export {
  CHANNEL,
  DEFAULT_PROTOCOL_VERSION,
  FIT_MESSAGE,
  RESIZE_MESSAGE,
} from "./core/constants.js";

const OPAQUE_ORIGIN = "null";

function resolveWindow(environment?: BridgeEnvironment): WindowLike {
  const candidate =
    environment?.window ??
    (globalThis as unknown as { window?: WindowLike }).window;
  if (!candidate) {
    throw new Error(
      "createHostBridge: no window available. Pass `{ window }` when running outside a browser.",
    );
  }
  return candidate;
}

/**
 * The trusted (parent-page) side of the bridge.
 *
 * It waits for the content side's `connect`, validates the sender window and
 * origin, negotiates `protocol_version`, and replies with `init` (carrying the
 * assigned `component_id`) or `reject`.
 */
class HostBridge extends BaseBridge {
  private readonly iframe: Pick<HTMLIFrameElement, "contentWindow">;
  private readonly allowedOrigin: string;
  private readonly allowInsecureOpaqueOrigin: boolean;

  constructor(config: HostBridgeConfig, environment?: BridgeEnvironment) {
    super({
      protocolVersion: config.protocolVersion ?? DEFAULT_PROTOCOL_VERSION,
      componentId: config.componentId,
      // The host is web-only: default to the window/postMessage transport.
      transport: environment?.transport ?? new WindowTransport(resolveWindow(environment)),
      rolePrefix: "host",
    });
    this.iframe = config.iframe;
    this.allowedOrigin = config.allowedOrigin;
    this.allowInsecureOpaqueOrigin = config.allowInsecureOpaqueOrigin ?? false;
  }

  protected receive(envelope: Envelope, event: MessageEventLike): void {
    switch (envelope.kind) {
      case "connect":
        this.handleConnect(envelope, event);
        break;
      case "message":
      case "request":
      case "response":
      case "error":
        this.acceptOpenFrame(envelope, event);
        break;
      default:
        // `init` / `reject` are content-directed; the host never consumes them.
        break;
    }
  }

  protected isExpectedSource(source: unknown): boolean {
    const contentWindow = this.iframe.contentWindow;
    return contentWindow !== null && source === contentWindow;
  }

  protected isAllowedOrigin(origin: string): boolean {
    if (origin === this.allowedOrigin) return true;
    if (origin === OPAQUE_ORIGIN && this.allowInsecureOpaqueOrigin) return true;
    return false;
  }

  private handleConnect(envelope: Envelope, event: MessageEventLike): void {
    if (this.open) return;
    if (!this.isExpectedSource(event.source)) return;
    if (!this.isAllowedOrigin(event.origin)) return;

    const replyTargetOrigin = this.replyTargetOrigin(event.origin);
    const source = event.source as WindowLike;

    // Version negotiation: a version this host does not support is refused and the
    // channel is never opened.
    if (envelope.protocol_version !== this.protocolVersion) {
      const rejection = this.buildEnvelope({
        kind: "reject",
        error: `Unsupported protocol_version ${envelope.protocol_version}; host supports ${this.protocolVersion}`,
      });
      this.transport.post(rejection, {
        window: source,
        targetOrigin: replyTargetOrigin,
      });
      return;
    }

    // Accept: pin the peer, reply with `init` (carrying component_id +
    // protocol_version), and open the channel.
    this.peerWindow = (this.iframe.contentWindow as WindowLike | null) ?? source;
    this.peerOrigin = replyTargetOrigin;
    const init = this.buildEnvelope({ kind: "init" });
    this.rawPost(init);
    this.emitReady();
  }

  private replyTargetOrigin(inboundOrigin: string): string {
    // Opaque origins ("null") can only be reached with "*"; we only get here when
    // `allowInsecureOpaqueOrigin` already accepted the inbound origin.
    if (inboundOrigin === OPAQUE_ORIGIN) return "*";
    return this.allowedOrigin;
  }
}

/**
 * Create the host side of the bridge. Attaches a `message` listener immediately;
 * the channel opens once the content side connects with a supported
 * `protocol_version`.
 *
 * @example
 * ```ts
 * const bridge = createHostBridge({
 *   iframe,
 *   allowedOrigin: "https://sdk.example.com",
 *   componentId: "checkout-1",
 *   protocolVersion: 1,
 * });
 * await bridge.whenReady();
 * bridge.send("hello", { at: Date.now() });
 * const pong = await bridge.request("ping", { n: 1 });
 * ```
 */
export function createHostBridge(
  config: HostBridgeConfig,
  environment?: BridgeEnvironment,
): Bridge {
  return new HostBridge(config, environment);
}
